import { useAuth } from '../contexts/AuthContext';
import { createApiHeadersGetter } from '../utils/api-headers';
import { USERS_URL } from '../config/api-config';
import { UserData } from '../types';

interface APIError {
  message: string;
  code?: string;
  details?: any;
}

interface UpdateUserData {
  isActive: boolean;
  isAdmin: boolean;
}

export const useUsersApi = () => {
  const { getAuthToken } = useAuth();
  const getHeaders = createApiHeadersGetter(getAuthToken);

  const getUsers = async (): Promise<UserData[]> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(USERS_URL, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      return data.users;  // API returns { users: UserData[] }
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  };

  const getUser = async (userId: string): Promise<UserData> => {
    try {
      console.log('[getUser] Fetching user:', userId);
      const headers = await getHeaders();
      const response = await fetch(`${USERS_URL}/${userId}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user details');
      }

      return response.json();
    } catch (error) {
      console.error('Error fetching user details:', error);
      throw error;
    }
  };

  const updateUser = async (userId: string, userData: UpdateUserData): Promise<UserData> => {
    try {
      console.log('[updateUser] Updating user:', userId, userData);
      const headers = await getHeaders();
      const response = await fetch(`${USERS_URL}/${userId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        console.log('[updateUser] Error updating user response:', response);
        try {
          const errorData: APIError = await response.json();
          console.log('[updateUser] Error updating user errorData:', errorData);
          throw new Error(errorData.message || 'Failed to update user');
        } catch (parseError) {
          throw new Error(String(parseError));
        }
      }

      return response.json();
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  };

  const createUser = async (user: Omit<UserData, 'userId'>): Promise<UserData> => {
    try {
      console.log('[createUser] Creating user:', user);
      const headers = await getHeaders();
      const response = await fetch(USERS_URL, {
        method: 'POST',
        headers,
        body: JSON.stringify(user)
      });

      if (!response.ok) {
        console.log('[createUser] Error creating user response:', response);
        try {
          const errorData: APIError = await response.json();
          console.log('[createUser] Error creating user errorData:', errorData);
          throw new Error(errorData.message || 'Failed to create user');
        } catch (parseError) {
          throw new Error(String(parseError));
        }
      }

      return response.json();
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  };

  return {
    getUsers,
    getUser,
    createUser,
    updateUser
  };
};